// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Collapse } from "react-bootstrap";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { ChargerConnectionType, ICloudParameter } from "shared/types/cloudtypes";
import { isSet } from "shared/utils/utils";
import { MainPanel } from "types";
import { IAction, ActionType, raiseAction } from "actions";
import { IState } from "states/states";
import { IConnectorConfig, IOcppCharger, IOcppChargingSession } from "states/ocppstates";
import { isVirtualOcppCharger } from "guiutils";

import ModalChargerSettings from "view/modal/ModalChargerSettings";
import ChargerList from "../comp/ChargerList";
import ChargerStatistics from "view/comp/ChargerStatistics";
import ChargerParameter from "view/comp/ChargerParameter";
import ChargerData from "view/comp/ChargerData";
import ChargingSessionList from "./ChargingSessionList";
import OcppCommandButtons from "view/comp/OcppCommandButtons";
import SiteBar, { ISiteBarSearchFieldProps } from "view/comp/SiteBar";

import IconExpandMore from "icons/iconExpandMore";
import IconExpandLess from "icons/iconExpandLess";
import IconPrint from "icons/iconPrint";

interface IChargerPageProps {
  isChargerDetail: boolean;
  ocppChargers: IOcppCharger[];
  selectedCharger: number | undefined; // ocpp charger id
  selectedConnectorId: number | undefined;
  selectedConnector: IConnectorConfig | undefined;
  selectedChargerNrConnectors: number | undefined;
  canModifyChargers: boolean;
  totalConnectors: number;
  selectedChargingSession: number | undefined;
  pendingChargingSessions: IOcppChargingSession[];
  isRfidUser: boolean;
  isVirtual: boolean;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const totalConnectors = state.ocpp.charger.reduce(
    (prev, curCharger, curIndex) =>
      prev +
      (curCharger.numberofconnectors ?? (state.ocpp.connectorData[curIndex]?.length ?? 2) - 1),
    0,
  );
  const chargerId = state.router.charger;
  const chargerIndex = state.ocpp.charger.findIndex((curItem) => curItem.fk_charger === chargerId);
  const selectedOcppCharger = chargerIndex >= 0 ? state.ocpp.charger[chargerIndex] : undefined;

  const props: IChargerPageProps = {
    isChargerDetail: state.router.panel === MainPanel.CHARGERDETAIL,
    ocppChargers: state.ocpp.charger,
    totalConnectors,
    selectedCharger: state.router.charger,
    selectedConnectorId: state.router.connector,
    selectedConnector:
      chargerIndex >= 0 ?
        state.ocpp.connectorConfig[chargerIndex]?.[state.router.connector ?? 1]
      : undefined,
    canModifyChargers:
      (curUser?.accessRights.ocppSettings || curUser?.accessRights.ocppChargerAdmin) ?? false,
    selectedChargerNrConnectors:
      selectedOcppCharger?.numberofconnectors ??
      state.ocpp.connectorData[chargerIndex!]?.length - 1,
    selectedChargingSession: state.router.transaction,
    pendingChargingSessions: state.ocpp.pendingChargingSession,
    isRfidUser: !!state.site.user?.isRfidUser,
    isVirtual: isVirtualOcppCharger(
      (state.para.parameter as ICloudParameter | undefined)?.virtualSite?.ocpp,
      chargerId,
      state.cached.selectedSite,
    ),
  };
  return props;
};

interface IChargerPageState {
  isExpanded: boolean;
}

interface IChargerPageDispatch {
  onEdit: (id: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargerPageDispatch = {
    onEdit: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_CHARGER, id));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  connector: {
    id: "Connector",
  },
  editHint: {
    id: "change settings",
  },
  printHint: {
    id: "print",
  },
  placeholderSearchField: {
    id: "Search for charging sessions...",
  },
  toolTipSearchField: {
    id: "Search for charger name, vendor, serial number, user name, card number or category. To search for multiple fields, separate search texts by a space!",
  },
});

class ChargerPageComponent extends React.Component<
  IChargerPageProps & IChargerPageDispatch & RoutedProps,
  IChargerPageState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public state: IChargerPageState = {
    isExpanded: false,
  };

  public render() {
    const searchFieldProps: ISiteBarSearchFieldProps = {
      placeholder: messages.placeholderSearchField,
      toolTip: messages.toolTipSearchField,
      dataListId: "chargerOptions",
    };
    return (
      <>
        {this.props.isRfidUser !== true && (
          <SiteBar showPeriodSelect={true} searchFieldProps={searchFieldProps} />
        )}
        <div id="chargerpage" className="row">
          {this.props.isChargerDetail && (
            <div className="flex-content d-print-none">
              <Button
                id="navigate-back"
                className="d-md-none"
                variant="secondary"
                onClick={() => this.props.navigate(-1)}
              >
                <FormattedMessage id="Back to Charger List" />
              </Button>
            </div>
          )}
          {this.renderLeft()}
          {this.props.ocppChargers.length > 0 &&
            (this.props.isRfidUser ? this.renderRightRfidUser() : this.renderRight())}
        </div>
        <ModalChargerSettings />
      </>
    );
  }

  private renderLeft() {
    return (
      <div
        id="chargerpage-left"
        className={`d-print-none ${
          this.props.isChargerDetail ? "d-none d-md-block" : "col-12"
        } col-md-5 col-lg-4 col-xl-4`}
      >
        <h1>
          <FormattedMessage id="Charger List" />
        </h1>
        {(this.props.totalConnectors > 3 || this.props.isRfidUser) && <ChargerStatistics />}
        {!this.props.isRfidUser && this.renderChargerList()}
      </div>
    );
  }

  private renderChargerList() {
    return (
      <div id="chargerinfo">
        <ChargerList isDashboard={false} />{" "}
      </div>
    );
  }

  private renderRightRfidUser() {
    return (
      <div id="chargerpage-right" className="d-md-block d-print-block col-md-7 col-lg-8 col-xl-8">
        {this.renderChargerList()}
      </div>
    );
  }

  private renderRight() {
    const chargerId = this.props.selectedCharger;
    const charger = this.props.ocppChargers.find((curItem) => curItem.fk_charger === chargerId);
    const pendingSession = this.props.pendingChargingSessions.find(
      (curItem) =>
        curItem.fk_charger === chargerId &&
        curItem.connector === (this.props.selectedConnectorId ?? 1),
    );
    const hasChargerControl =
      !charger?.connectionType || charger?.connectionType === ChargerConnectionType.PROXY;

    const connectorName =
      isSet(this.props.selectedConnector) && this.props.selectedChargerNrConnectors! > 1 ?
        <span className="nowrap">
          {this.getConnectorName(
            this.props.selectedConnectorId ?? 1,
            this.props.selectedConnector.name,
          )}
        </span>
      : undefined;

    return (
      <div
        id="chargerpage-right"
        style={isSet(charger) ? { overflow: "auto" } : undefined}
        className={`${
          this.props.isChargerDetail ? "col-12" : "d-none d-md-block d-print-block"
        } col-md-7 col-lg-8 col-xl-8`}
      >
        {isSet(charger) && (
          <div id="chargerinfo">
            <div id="chargerinfo-heading">
              <h1
                className="link"
                onClick={() => {
                  this.setState({ isExpanded: !this.state.isExpanded });
                }}
              >
                <Button variant="link" className="expand-button me-2 d-print-none">
                  {this.state.isExpanded ?
                    <IconExpandMore />
                  : <IconExpandLess />}
                </Button>
                {charger?.name ?? charger?.chargepoint}
                {connectorName ? ` - ` : ""}
                {connectorName}
              </h1>
              <div className="buttonbar d-print-none">
                {this.props.canModifyChargers && !this.props.isVirtual && this.state.isExpanded && (
                  <Button
                    id="editCharger"
                    className="nowrap"
                    variant="secondary"
                    title={this.context.formatMessage(messages.editHint)}
                    onClick={(_evt) => this.props.onEdit(chargerId!)}
                  >
                    <FormattedMessage id="Edit Charger" />
                  </Button>
                )}
                <Button
                  variant="secondary"
                  title={this.context.formatMessage(messages.printHint)}
                  onClick={() => {
                    window.print();
                  }}
                >
                  <IconPrint />
                </Button>
              </div>
            </div>
            <Collapse in={this.state.isExpanded}>
              <div className="row">
                {hasChargerControl && (
                  <OcppCommandButtons chargerId={charger!.fk_charger!} session={pendingSession} />
                )}
                <div className="col-12 col-sm-6 col-md-12 col-lg-6 mb-3">
                  <ChargerParameter />
                </div>
                <div className="col-12 col-sm-6 col-md-12 col-lg-6 mb-4">
                  <ChargerData isSessionView={false} />
                </div>
              </div>
            </Collapse>
          </div>
        )}
        <ChargingSessionList isElementSelected={isSet(charger)} />
      </div>
    );
  }

  private getConnectorName(conIndex: number, connectorName: string | undefined) {
    if (connectorName) {
      return connectorName;
    }
    // provide default name
    return `${this.context.formatMessage(messages.connector)} ${conIndex}`;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ChargerPageComponent));
